import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import { 
  Box, 
  Typography, 
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SubscriptionIcon from '../../../../assets/main/subscription-plan.svg';
import TotalOrgIcon from '../../../../assets/organization/total-organization-icon.svg';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { useSidebar } from '../../../../context/SidebarContext';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import { 
  fetchSubscriptionPlanDetail, 
  selectCurrentPlan, 
  selectCurrentPlanLoading,
  clearCurrentPlan,
} from '../../../../modules/subscription';
import apiCall from '../../../../utils/api';
import { CircularProgress } from '@mui/material';
import Loader from '../../../../components/common/Loader';

import {
  PageWrapper,
  DetailCard,
  CardHeader,
  PlanInfo,
  PlanIconBox,
  StatusBadge as PlanStatusBadge,
  HeaderActions,
  EditButton,
  Section,
  SectionHeader,
  SectionContent,
  DataItem,
  DataLabel,
  DataValue,
  FeatureGrid,
  FeatureBadge,
} from './styles';

import { 
  HeaderCard, 
  IconWrapper, 
  HeaderInfo, 
  StatusBadge as OrgStatusBadge 
} from '../Overview/styles';

const OrganizationSubscriptionDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  const [orgLoading, setOrgLoading] = React.useState(true);
  const [actualPlanId, setActualPlanId] = React.useState<string | null>(null);
  const [orgData, setOrgData] = React.useState<any>(null);

  const planData = useSelector(selectCurrentPlan);
  const loading = useSelector(selectCurrentPlanLoading);

  const fetchOrgDetails = React.useCallback(async () => {
    try {
      setOrgLoading(true);
      const response = await apiCall<any>('get', `/api/organization/superadmin/detail/${id}/`);
      if (response.data && response.data.data) {
        setOrgData(response.data.data);
        const planId = response.data.data.plan_details?.plan_id;
        if (planId) {
          setActualPlanId(planId.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching org details:', error);
    } finally {
      setOrgLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrgDetails();
    return () => {
      dispatch(clearCurrentPlan());
    };
  }, [fetchOrgDetails, dispatch]);

  useEffect(() => {
    setActivePage('organizations');
    setPath([
      { title: 'Super Admin', path: '/super-admin' },
      { title: 'Organizations', path: '/super-admin/organizations' },
      { title: orgData?.org_name || 'Overview', path: `/super-admin/organizations/${id}` },
      { title: 'Subscription Plan', path: `/super-admin/organizations/${id}/subscription` },
    ]);
    setIsShow(true);
  }, [setActivePage, setPath, setIsShow, orgData, id]);

  useEffect(() => {
    if (actualPlanId) {
      dispatch(fetchSubscriptionPlanDetail(actualPlanId));
    }
  }, [dispatch, actualPlanId]);

  if (orgLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageWrapper>
      {loading && <Loader open={loading} />}

      {orgData && (
        <HeaderCard elevation={0} sx={{ mb: 3 }}>
          <IconWrapper>
            <img src={TotalOrgIcon} alt="Org Icon" style={{ width: '32px', height: '32px', filter: 'grayscale(100%)' }} />
          </IconWrapper>
          <HeaderInfo>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
              {orgData.org_name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <Typography variant="body2" sx={{ color: '#666', mr: 1 }}>
                ID: {orgData.org_id}
              </Typography>
              <OrgStatusBadge status={orgData.status}>
                {orgData.status}
              </OrgStatusBadge>
            </Box>
          </HeaderInfo>
        </HeaderCard>
      )}
      
      {!planData && !loading ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '60vh',
          backgroundColor: '#fff',
          borderRadius: '12px',
          p: 4,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
        }}>
          <img src={SubscriptionIcon} alt="No Subscription" style={{ width: '80px', height: '80px', opacity: 0.3, marginBottom: '24px' }} />
          <Typography sx={{ mb: 4, fontWeight: 600, color: '#666', fontSize: '1.1rem' }}>
            No active subscription plan found for {orgData?.org_name || 'this organization'}.
          </Typography>
          <EditButton 
            variant="contained" 
            disableElevation
            onClick={() => navigate(`/super-admin/organizations/${id}/subscription/upgrade`)}
            sx={{ px: 6, py: 1.5, fontSize: '15px' }}
          >
            Assign a Subscription Plan
          </EditButton>
        </Box>
      ) : planData && (
        <DetailCard>
          <CardHeader>
            <PlanInfo>
              <PlanIconBox>
                <img src={SubscriptionIcon} alt="Plan" style={{ width: '32px', height: '32px' }} />
              </PlanIconBox>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A1C1E' }}>
                  {planData.plan_name}
                </Typography>
                <PlanStatusBadge active={planData.is_active}>
                  {planData.is_active ? 'Active' : 'Inactive'}
                </PlanStatusBadge>
              </Box>
            </PlanInfo>
            <HeaderActions>
              <EditButton 
                variant="contained" 
                disableElevation
                onClick={() => navigate(`/super-admin/organizations/${id}/subscription/upgrade`)}
              >
                Upgrade / Change Plan
              </EditButton>
            </HeaderActions>
          </CardHeader>

          <Box sx={{ p: 3 }}>
            {/* Section 1: Plan Details */}
            <Section>
              <SectionHeader>Plan Details</SectionHeader>
              <SectionContent>
                <DataItem sx={{ gridColumn: 'span 3' }}>
                  <DataLabel>Plan Name</DataLabel>
                  <DataValue>{planData.plan_name}</DataValue>
                </DataItem>
                <DataItem sx={{ gridColumn: 'span 3' }}>
                  <DataLabel>Description</DataLabel>
                  <DataValue>{planData.plan_description || 'N/A'}</DataValue>
                </DataItem>
                <DataItem sx={{ gridColumn: 'span 3' }}>
                  <DataLabel>Price</DataLabel>
                  <DataValue>${planData.plan_price_month}</DataValue>
                </DataItem>
                <DataItem sx={{ gridColumn: 'span 3' }}>
                  <DataLabel>Validity</DataLabel>
                  <DataValue>{planData.plan_expiry_in_days} Days</DataValue>
                </DataItem>
              </SectionContent>
            </Section>

            {/* Section 2: Subscription Validity */}
            {orgData?.plan_details && (
              <Section>
                <SectionHeader>Subscription Validity</SectionHeader>
                <SectionContent>
                  <DataItem sx={{ gridColumn: 'span 4' }}>
                    <DataLabel>Start Date</DataLabel>
                    <DataValue>{dayjs(orgData.plan_details.start_date).format('DD MMMM, YYYY')}</DataValue>
                  </DataItem>
                  <DataItem sx={{ gridColumn: 'span 4' }}>
                    <DataLabel>Expiry Date</DataLabel>
                    <DataValue>
                      {orgData.plan_details.end_date 
                        ? dayjs(orgData.plan_details.end_date).format('DD MMMM, YYYY') 
                        : 'N/A'}
                    </DataValue>
                  </DataItem>
                  <DataItem sx={{ gridColumn: 'span 4' }}>
                    <DataLabel>Standard Validity</DataLabel>
                    <DataValue>{planData.plan_expiry_in_days} Days</DataValue>
                  </DataItem>
                </SectionContent>
              </Section>
            )}

            {/* Section 3: Usage Limits */}
            <Section>
              <SectionHeader>Usage Limits</SectionHeader>
              <SectionContent>
                <DataItem sx={{ gridColumn: 'span 4' }}>
                  <DataLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Users Allowed
                    <Tooltip title="Maximum staff users allowed">
                      <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#999' }} />
                    </Tooltip>
                  </DataLabel>
                  <DataValue>{planData.number_of_staff_users}</DataValue>
                </DataItem>
                <DataItem sx={{ gridColumn: 'span 4' }}>
                  <DataLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Workers Allowed
                    <Tooltip title="Maximum workers allowed">
                      <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#999' }} />
                    </Tooltip>
                  </DataLabel>
                  <DataValue>
                    {planData.number_of_workers === -1 ? 'Unlimited' : planData.number_of_workers}
                  </DataValue>
                </DataItem>
                <DataItem sx={{ gridColumn: 'span 4' }}>
                  <DataLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Facilities Count
                    <Tooltip title="Maximum facilities allowed">
                      <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#999' }} />
                    </Tooltip>
                  </DataLabel>
                  <DataValue>
                    {planData.number_of_facilities === -1 ? 'Unlimited' : planData.number_of_facilities}
                  </DataValue>
                </DataItem>
              </SectionContent>
            </Section>

            {/* Section 3: Plan Features */}
            <Section>
              <SectionHeader>Plan Features</SectionHeader>
              <FeatureGrid>
                {planData.modules_list && planData.modules_list.map((module: any, index: number) => (
                  <FeatureBadge key={`mod-${index}`}>
                    <CheckCircleIcon />
                    {module.system_module_name || module.module_name || 'Unknown Module'}
                  </FeatureBadge>
                ))}
                
                {(planData as any).plan_modules && (planData as any).plan_modules.map((module: any, index: number) => (
                  <FeatureBadge key={`pmod-${index}`}>
                    <CheckCircleIcon />
                    {module.system_module_details?.module_name || 
                     module.system_module_name || 
                     module.module_name || 
                     module.name || 
                     'Unknown Module'}
                  </FeatureBadge>
                ))}

                {planData.features && typeof planData.features === 'string' && planData.features !== 'N/A' && 
                  planData.features.split(',').map((feature: string, index: number) => (
                    <FeatureBadge key={`feat-${index}`}>
                      <CheckCircleIcon />
                      {feature.trim()}
                    </FeatureBadge>
                  ))
                }

                {!planData.modules_list?.length && 
                 !(planData as any).plan_modules?.length && 
                 (!planData.features || planData.features === 'N/A') && (
                  <Typography color="textSecondary" sx={{ p: 2 }}>No features assigned.</Typography>
                )}
              </FeatureGrid>
            </Section>
          </Box>
        </DetailCard>
      )}
    </PageWrapper>
  );
};

export default OrganizationSubscriptionDetailsPage;
